import { useCallback } from 'react';
import { eachDayOfInterval, eachHourOfInterval } from 'date-fns';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
} from 'recharts';
import { Box } from '@cbhq/cds-web/layout';
import { TextHeadline } from '@cbhq/cds-web/typography';

import { ChartType as MonitoringCharType } from ':cloud/widgets/earnings/types';
import { TotalOverlay } from ':cloud/widgets/monitoring/TotalOverlay';
import { ChartType as EarningsChartType, DashboardRange } from ':cloud/widgets/monitoring/types';

import { formatTickTimestamp, getEndTime, getStartTime } from '../monitoring/utils';

type ChartType = MonitoringCharType | EarningsChartType;

function generatePlaceholderBarData({ range = 30 }: { range?: DashboardRange } = {}) {
  const hours = eachHourOfInterval({
    start: getStartTime(range),
    end: getEndTime(range),
  });

  const days = eachDayOfInterval({
    start: getStartTime(range),
    end: getEndTime(range),
  });

  const placeholderData = () => {
    if (range === 1) {
      return hours.map((hour) => ({
        name: hour.toISOString(),
        value: 0,
      }));
    }
    return days.map((day) => ({
      name: day.toISOString(),
      value: 0,
    }));
  };

  return placeholderData();
}

const placeholderLinearData = [
  {
    name: '',
    value: 3,
  },
  {
    name: '',
    value: 4,
  },
  {
    name: '',
    value: 6,
  },
  {
    name: '',
    value: 7,
  },
  {
    name: '',
    value: 6,
  },
  {
    name: '',
    value: 10,
  },
  {
    name: '',
    value: 10,
  },
  {
    name: '',
    value: 12,
  },
  {
    name: '',
    value: 13,
  },
  {
    name: '',
    value: 14,
  },
  {
    name: '',
    value: 13,
  },
  {
    name: '',
    value: 14,
  },
  {
    name: '',
    value: 15,
  },
  {
    name: '',
    value: 14,
  },
  {
    name: '',
    value: 18,
  },
  {
    name: '',
    value: 18,
  },
  {
    name: '',
    value: 17,
  },
  {
    name: '',
    value: 20,
  },
];

const xAxisProps = { padding: { left: 0, right: 0 }, tickLine: false, stroke: 'var(--line)' };

type EmptyChartProps = {
  chartType: ChartType;
  showOverlay?: boolean;
  range?: DashboardRange;
};

type EmptyChartBodyProps = {
  data: Record<string, string | number>[];
  range?: DashboardRange;
} & Pick<EmptyChartProps, 'showOverlay'>;

function EmptyChartTextOverlay() {
  return (
    <Box position="relative" display="block" top="-50%">
      <TextHeadline as="h3" align="center">
        No data to show
      </TextHeadline>
    </Box>
  );
}

function EmptyAreaChart({ data, showOverlay }: EmptyChartBodyProps) {
  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart width={150} height={40} data={data}>
          <Area type="monotone" dataKey="value" fill="var(--secondary)" stroke="var(--secondary)" />
          <XAxis dataKey="time" {...xAxisProps} />
        </AreaChart>
      </ResponsiveContainer>
      {showOverlay && <EmptyChartTextOverlay />}
    </>
  );
}

function EmptyBarChart({ data, showOverlay, range = 7 }: EmptyChartBodyProps) {
  const xAxisTickFormatter = useCallback(
    (tickItem: string) => {
      return formatTickTimestamp(tickItem, range);
    },
    [range],
  );
  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart width={150} height={40} data={data}>
          <Bar dataKey="value" fill="var(--secondary)" />
          <XAxis
            dataKey="name"
            {...xAxisProps}
            tickFormatter={xAxisTickFormatter}
            interval="equidistantPreserveStart"
            minTickGap={20}
          />
        </BarChart>
      </ResponsiveContainer>
      {showOverlay && <EmptyChartTextOverlay />}
      {showOverlay && <TotalOverlay value="" />}
    </>
  );
}

function EmptyLineChart({ data, showOverlay }: EmptyChartBodyProps) {
  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart width={150} height={40} data={data}>
          <Line type="monotone" dataKey="value" stroke="var(--line)" strokeWidth={2} />
          <XAxis dataKey="time" {...xAxisProps} />
        </LineChart>
      </ResponsiveContainer>
      {showOverlay && <EmptyChartTextOverlay />}
    </>
  );
}

type EmptyChartComponentMap = {
  [key in EmptyChartProps['chartType']]: JSX.Element;
};

export function EmptyChart({ chartType, range, showOverlay = true }: EmptyChartProps) {
  const EmptyChartComponent: EmptyChartComponentMap = {
    area: <EmptyAreaChart data={placeholderLinearData} showOverlay={showOverlay} />,
    bar: (
      <EmptyBarChart
        data={generatePlaceholderBarData({ range })}
        showOverlay={showOverlay}
        range={range}
      />
    ),
    line: <EmptyLineChart data={placeholderLinearData} showOverlay={showOverlay} />,
    // TODO: CCPLT-1001 earnings balance chart empty graph
    pie: <EmptyLineChart data={placeholderLinearData} showOverlay={showOverlay} />,
  };

  return EmptyChartComponent[chartType];
}
